import { useFileInfo } from "@/api/services/global/file";
import { IconClose, IconDownloadBlue, IconSearchBlue } from "@/assets/icons";
import { IconBox } from "@/components/elements/IconBox";
import { FILE_EXISTED, MIME_TYPES } from "@/constants/file";
import { EMPTY_STR } from "@/constants/string";
import { TalentImagePreviewModal } from "@/features/talent/components/talent-detail/TalentImagePreviewModal";
import { TTalentUploadFile } from "@/features/talent/types/talentForm";
import { useDialog } from "@/hooks/useDialog";
import { background, button, primary, text } from "@/theme/colors";
import { textEllipsis } from "@/theme/minxin";
import { typography } from "@/theme/typography";
import { TRequiredOne } from "@/types/common";
import { TExtendFile } from "@/types/file";
import { showError } from "@/utils/error";
import { downloadFileByUrl, downloadFileFromPath } from "@/utils/export";
import { convertByteToMb } from "@/utils/file";
import { truncateFileNameMiddle } from "@/utils/string";
import { Box, Skeleton, Stack, StackProps, Tooltip, Typography, css, styled } from "@mui/material";
import { FC, useState } from "react";

type TFileWithPath = { filePath: string; name?: string };
type TFileList = TExtendFile[] | TTalentUploadFile[] | TFileWithPath[];

type TTalentFileItemProps = {
  onRemove?: () => void;
  currentFileIndex?: number;
  errorMsg?: string;
  downloadable?: boolean;
  previewEnabled?: boolean;
  hideIcon?: boolean;
  enableFileSize?: boolean;
  files?: TFileList | null;
} & TRequiredOne<{
  file: File;
  filePath?: string;
}> &
  StackProps;

const PREVIEWABLE_MIME_TYPES = [...MIME_TYPES.IMAGE, ...MIME_TYPES.PDF];

export const TalentFileItem: FC<TTalentFileItemProps> = ({ currentFileIndex, files, file, filePath, downloadable, previewEnabled, ...rest }) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const handleDownloadFile = async () => {
    if (!downloadable) return;
    try {
      setIsDownloading(true);
      let url = "";
      if (file) {
        url = URL.createObjectURL(file);
        await downloadFileByUrl(file?.name, url);
      }
      if (!file && filePath) {
        await downloadFileFromPath(filePath);
      }
    } catch (error) {
      showError(error);
    } finally {
      setIsDownloading(false);
    }
  };
  const { data: infoResult } = useFileInfo(filePath);
  const infoData = infoResult?.data;

  const normalizedFiles = files && Array.isArray(files) ? files : undefined;

  if (file && infoData) {
    return (
      <TalentFileItemContent
        currentFileIndex={currentFileIndex}
        filePath={filePath}
        files={normalizedFiles}
        fileName={file.name}
        fileSize={file.size}
        mimeType={infoData.mimeType}
        downloadable={downloadable}
        previewEnabled={previewEnabled}
        isDownloading={isDownloading}
        onDownload={handleDownloadFile}
        {...rest}
      />
    );
  }
  if (filePath) {
    return (
      <FilePathItem
        currentFileIndex={currentFileIndex}
        files={normalizedFiles}
        filePath={filePath}
        previewEnabled={previewEnabled}
        isDownloading={isDownloading}
        downloadable={downloadable}
        onDownload={handleDownloadFile}
        {...rest}
      />
    );
  }
  return null;
};

type TFilePathItemProps = {
  currentFileIndex?: number;
  files?: TFileList;
  filePath: string;
  previewEnabled?: boolean;
  onDownload: () => void;
  isDownloading: boolean;
} & TTalentFileItemProps;

const FilePathItem: FC<TFilePathItemProps> = ({ currentFileIndex, files, filePath, previewEnabled, onDownload, ...rest }) => {
  if (!filePath || filePath === FILE_EXISTED) return null;
  const { data: infoResult, isLoading } = useFileInfo(filePath);
  const infoData = infoResult?.data;

  if (!infoData && !isLoading) return null;
  return (
    <TalentFileItemContent
      currentFileIndex={currentFileIndex}
      filePath={filePath}
      files={files}
      isLoading={isLoading}
      fileName={infoData?.name || EMPTY_STR.TEXT}
      fileSize={infoData?.size || 0}
      mimeType={infoData?.mimeType || EMPTY_STR.TEXT}
      previewEnabled={previewEnabled}
      onDownload={onDownload}
      {...rest}
    />
  );
};

type TTalentFileItemContentProps = {
  onRemove?: () => void;
  onDownload: () => void;
  errorMsg?: string;
  downloadable?: boolean;
  previewEnabled?: boolean;
  currentFileIndex?: number;
  fileName: string;
  fileSize: number;
  mimeType: string;
  isLoading?: boolean;
  isDownloading?: boolean;
  hideIcon?: boolean;
  enableFileSize?: boolean;
  filePath?: string;
  files?: TFileList;
} & StackProps;

const TalentFileItemContent: FC<TTalentFileItemContentProps> = ({
  onRemove,
  onDownload,
  errorMsg,
  downloadable = false,
  previewEnabled = false,
  isLoading = false,
  isDownloading = false,
  currentFileIndex = 0,
  fileName,
  fileSize,
  mimeType,
  hideIcon: _hideIcon = false,
  enableFileSize = false,
  filePath,
  files = [],
  ...rest
}) => {
  const imagePreview = useDialog();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const displayFileName = truncateFileNameMiddle(fileName, 30);

  const handleNextImage = () => {
    if (files && files.length > 0) {
      setCurrentImageIndex((prev) => (prev + 1) % files.length);
    }
  };

  const handlePrevImage = () => {
    if (files && files.length > 0) {
      setCurrentImageIndex((prev) => (prev - 1 + files.length) % files.length);
    }
  };

  return (
    <>
      <Stack {...rest} gap={0.5}>
        <Stack direction="row" alignItems="center" gap={1}>
          <TalentFileItemWrap error={!!errorMsg} isDownloading={isDownloading}>
            <Tooltip
              arrow
              slotProps={{
                tooltip: {
                  sx: {
                    backgroundColor: background.white,
                    color: text.primary,
                    boxShadow: "0px 2px 8px 0px #00000040",
                  },
                },
                arrow: {
                  sx: {
                    color: background.white,
                  },
                },
              }}
              title={
                <Typography
                  variant="cap12"
                  sx={{
                    backgroundColor: background.white,
                    color: text.primary,
                  }}
                >
                  {fileName}
                </Typography>
              }
            >
              <FileName title={fileName}>{isLoading ? <Skeleton variant="text" height={32} width={200} /> : displayFileName}</FileName>
            </Tooltip>
          </TalentFileItemWrap>
          {!enableFileSize || isLoading ? null : (
            <Typography variant="body14" whiteSpace="nowrap" flexShrink={0}>
              {convertByteToMb(fileSize).toFixed(2)}MB
            </Typography>
          )}
          {onRemove && (
            <IconBox onClick={onRemove} sx={{ flexShrink: 0 }}>
              <IconClose />
            </IconBox>
          )}
          <Stack direction="row" alignItems="center" gap={1} sx={{ marginLeft: "16px" }}>
            {previewEnabled && PREVIEWABLE_MIME_TYPES.includes(mimeType) && (
              <IconBox sx={{ width: 20, height: 20, padding: 0, flexShrink: 0 }} onClick={() => imagePreview.show()}>
                <IconSearchBlue width={20} height={20} color={primary[500]} />
              </IconBox>
            )}
            {downloadable && (
              <IconBox
                sx={{
                  width: 20,
                  height: 20,
                  padding: 0,
                  flexShrink: 0,
                  cursor: isDownloading ? "not-allowed" : "pointer",
                  opacity: isDownloading ? 0.5 : 1,
                }}
                onClick={isDownloading ? undefined : onDownload}
                disabled={isDownloading}
              >
                <IconDownloadBlue color={isDownloading ? button.disable : primary[500]} width={20} height={20} />
              </IconBox>
            )}
          </Stack>
        </Stack>
        {errorMsg && (
          <Typography variant="body14" color={text.error} whiteSpace="pre-line">
            {errorMsg}
          </Typography>
        )}
      </Stack>
      {previewEnabled && files && files.length > 0 && (
        <TalentImagePreviewModal
          currentFileIndex={currentFileIndex}
          filePath={filePath || (files[currentImageIndex] as TFileWithPath | TTalentUploadFile)?.filePath}
          file={files[currentImageIndex] instanceof File ? (files[currentImageIndex] as File) : undefined}
          imageList={files}
          open={imagePreview.open}
          onClose={imagePreview.cancel}
          onNext={handleNextImage}
          onPrev={handlePrevImage}
        />
      )}
    </>
  );
};

const options = { shouldForwardProp: (propName: string) => !["error", "isDownloading"].includes(propName) };

const TalentFileItemWrap = styled(Box, options)<{ error: boolean; isDownloading: boolean }>`
  display: flex;
  align-items: center;
  min-width: 0;
  gap: 8px;
  ${({ error }) => error && TalentFileItemError}
  ${({ isDownloading }) => isDownloading && TalentFileItemDownloading}
`;

const TalentFileItemDownloading = css`
  pointer-events: none;
  opacity: 0.8;
`;

const TalentFileItemError = css`
  &,
  button {
    color: ${text.error};
  }
`;

const FileName = styled(Typography)`
  ${textEllipsis};
  ${typography.body14};
`;

const _MimeType = styled(Typography)`
  width: 64px;
  max-width: 64px;
  ${textEllipsis};
  ${typography.body14};
  flex-shrink: 0;
`;
